import { ref, computed } from 'vue'

// Shared, module-level store for GET /api/settings/business.
// Replaces the independent fetches (App, Footer, Impressum, Terms, booking modal)
// and the 1h sessionStorage cache that used to serve stale business data.

const settings = ref(null)
const isLoaded = ref(false)
const error = ref(null)
let inflight = null

// Date#getDay() order
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const load = () => {
    if (isLoaded.value) return Promise.resolve(settings.value)
    if (inflight) return inflight

    inflight = (async () => {
        try {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 5000)
            const response = await fetch('/api/settings/business', { signal: controller.signal })
            clearTimeout(timeoutId)

            if (!response.ok) throw new Error(`Business settings API failed: ${response.status}`)
            const data = await response.json()
            if (!data.success || !data.data) throw new Error(data.message || 'Business settings unavailable')

            settings.value = data.data
            isLoaded.value = true
            error.value = null
        } catch (err) {
            error.value = err
            console.warn('[BusinessSettings]', err.message)
        } finally {
            inflight = null
        }
        return settings.value
    })()

    return inflight
}

const str = (key, fallback = '') => computed(() => settings.value?.[key] ?? fallback)

const businessName = str('businessName')
const businessMotto = str('businessMotto')
const businessAddress = str('businessAddress')
const businessEmail = str('businessEmail')
const businessPhone = str('businessPhone')
const businessOwner = str('businessOwner')

// Both flags default to true (the API only ever sends false to hide them).
const showHeroText = computed(() => settings.value?.showHeroText !== false)
const showLogo = computed(() => settings.value?.showLogo !== false)

const openingHours = computed(() => settings.value?.openingHours || {})

const refundPolicy = computed(() => ({
    refundEnabled: settings.value?.refundEnabled ?? false,
    refundWindowHours: settings.value?.refundWindowHours ?? 24,
    refundAllowPartial: settings.value?.refundAllowPartial ?? false
}))

const todayKey = computed(() => DAY_KEYS[new Date().getDay()])

const todayHours = computed(() => openingHours.value[todayKey.value] || null)

const toMinutes = (hhmm) => {
    if (typeof hhmm !== 'string') return null
    const [h, m] = hhmm.split(':').map(Number)
    if (Number.isNaN(h) || Number.isNaN(m)) return null
    return h * 60 + m
}

// { state: 'open' | 'closed' | 'opensLater', until, at } — local time, no tz maths.
const openStatus = computed(() => {
    const hours = todayHours.value
    if (!hours || !hours.open || !hours.close) return { state: 'closed', until: null, at: null }

    const opensAt = toMinutes(hours.open)
    const closesAt = toMinutes(hours.close)
    if (opensAt === null || closesAt === null) return { state: 'closed', until: null, at: null }

    const now = new Date()
    const nowMinutes = now.getHours() * 60 + now.getMinutes()

    if (nowMinutes < opensAt) return { state: 'opensLater', until: null, at: hours.open }
    if (nowMinutes < closesAt) return { state: 'open', until: hours.close, at: null }
    return { state: 'closed', until: null, at: null }
})

export const useBusinessSettings = () => ({
    settings,
    isLoaded,
    error,
    load,
    businessName,
    businessMotto,
    businessAddress,
    businessEmail,
    businessPhone,
    businessOwner,
    showHeroText,
    showLogo,
    openingHours,
    refundPolicy,
    todayKey,
    todayHours,
    openStatus
})
